import { ApiRequestError, errorMessage, errorRequestId } from '../lib/api-client.js'
import { cn } from '../lib/cn.js'
import { Button } from './button.js'

/**
 * Панель ошибки — единственное место в интерфейсе, где показывается сбой.
 *
 * Три вещи, которые она обязана дать человеку, и в этом порядке: что
 * случилось, что с этим делать, и по какой строке искать в логах. Третье
 * важнее, чем кажется: без идентификатора запроса обращение звучит как
 * «у меня не сохранилось», и на разбор уходит полдня.
 */
export function ErrorPanel({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  const requestId = errorRequestId(error)

  return (
    <div
      role="alert"
      className={cn('rounded-md border border-crimson/30 bg-crimson-soft p-4', className)}
    >
      <p className="text-sm font-medium text-crimson">{errorMessage(error)}</p>

      <p className="mt-1 text-xs text-ink-soft">{advice(error)}</p>

      {requestId && (
        <p className="mt-2 text-xs text-muted">
          Идентификатор запроса: <code className="font-mono select-all">{requestId}</code>
        </p>
      )}

      {onRetry && (
        <Button size="sm" className="mt-3" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  )
}

/**
 * Совет по виду ошибки. Формулировки прикладные: человеку нужно знать,
 * его это действие или чужая поломка, и стоит ли пробовать снова.
 */
function advice(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return 'Попробуйте обновить страницу. Если повторится — сообщите разработчику.'
  }

  switch (error.code) {
    case 'NETWORK_ERROR':
      return 'Похоже, сервер не запущен или недоступен. Проверьте, что backend работает.'
    case 'CONTRACT_MISMATCH':
      return 'Обновите страницу: скорее всего, интерфейс остался от прежней версии.'
    case 'VALIDATION_FAILED':
      return 'Проверьте заполненные поля — сервер счёл данные некорректными.'
    default:
      break
  }

  if (error.isUnauthorized) return 'Сессия истекла. Войдите заново.'
  if (error.isForbidden) return 'Для этого действия нужна роль выше. Обратитесь к владельцу.'
  if (error.isNotFound) return 'Запись не найдена — возможно, её уже удалили.'
  if (error.status >= 500) return 'Это сбой на сервере, а не в ваших данных. Повторите попытку.'

  return 'Проверьте введённые данные и попробуйте снова.'
}

/** Ошибки списка внутри экрана: подробности те же, места занимает меньше. */
export function InlineError({ error }: { error: unknown }) {
  const requestId = errorRequestId(error)

  return (
    <p role="alert" className="text-sm text-crimson">
      {errorMessage(error)}
      {requestId && <span className="ml-2 font-mono text-xs text-muted">{requestId}</span>}
    </p>
  )
}
